/**
 * Search Bar — Typing Animation Component
 */

class SearchBar {
  constructor(element) {
    this.root = element;
    this.form = this.root.querySelector('[data-search-bar-form]');
    this.input = this.root.querySelector('[data-search-bar-input]');
    this.placeholder = this.root.querySelector('[data-search-bar-placeholder]');
    this.labelsContainer = this.root.querySelector('[data-search-bar-labels]');

    this.labels = [];
    this.currentLabelIndex = 0;
    this.currentCharIndex = 0;
    this.animationPhase = 'typing'; // 'typing' | 'pause-after-type' | 'erasing' | 'pause-after-erase'
    this.timerId = null;

    // Timings (ms)
    this.TYPING_SPEED = 65;
    this.ERASING_SPEED = 35;
    this.PAUSE_AFTER_TYPE = 1500;
    this.PAUSE_AFTER_ERASE = 500;

    this.collectLabels();
    this.bindEvents();
    this.deferAnimationStart();

    // Référence stockée sur le DOM pour reinit via shopify:block:load
    this.root.__searchBarInstance = this;
  }

  /**
   * Démarre l'animation après idle + uniquement quand la search-bar est visible.
   * Évite ~100 ms de bootup au chargement initial.
   */
  deferAnimationStart() {
    const start = () => {
      if (this._started) return;
      this._started = true;
      this.startAnimation();
    };

    const schedule = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(start, { timeout: 2000 });
      } else {
        setTimeout(start, 800);
      }
    };

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          schedule();
        }
      }, { rootMargin: '50px' });
      io.observe(this.root);
    } else {
      schedule();
    }
  }

  collectLabels() {
    const labelElements = this.labelsContainer
      ? this.labelsContainer.querySelectorAll('[data-search-label]')
      : [];

    this.labels = [];
    labelElements.forEach((el) => {
      const text = el.textContent.trim();
      if (text) this.labels.push(text);
    });

    // Fallback : label par défaut si aucun child block
    if (this.labels.length === 0 && this.labelsContainer) {
      const defaultLabel = this.labelsContainer.dataset.defaultLabel;
      if (defaultLabel) this.labels.push(defaultLabel);
    }
  }

  bindEvents() {
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('focus', () => this.onFocus());
    this.input.addEventListener('blur', () => this.onBlur());

    // Pause animation when tab is hidden, resume when visible
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAnimation();
      } else if (this.input.value.length === 0) {
        this.resumeAnimation();
      }
    });
  }

  onInput() {
    if (this.input.value.length > 0) {
      this.pauseAnimation();
      this.form.classList.add('is-typing');
    } else {
      this.form.classList.remove('is-typing');
      this.resumeAnimation();
    }
  }

  onFocus() {
    this.pauseAnimation();
    this.form.classList.add('is-typing');
  }

  onBlur() {
    if (this.input.value.length === 0) {
      this.form.classList.remove('is-typing');
      this.resumeAnimation();
    }
  }

  pauseAnimation() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  resumeAnimation() {
    if (this.timerId) return;
    if (this.labels.length === 0) return;

    if (this.currentCharIndex > 0) {
      this.animationPhase = 'erasing';
      this.scheduleNext(this.ERASING_SPEED);
    } else {
      this.animationPhase = 'pause-after-erase';
      this.scheduleNext(this.PAUSE_AFTER_ERASE);
    }
  }

  startAnimation() {
    if (this.labels.length === 0) return;
    this.animationPhase = 'typing';
    this.currentLabelIndex = 0;
    this.currentCharIndex = 0;
    this.scheduleNext(this.TYPING_SPEED);
  }

  scheduleNext(delay) {
    this.timerId = setTimeout(() => {
      this.timerId = null;
      this.step();
    }, delay);
  }

  step() {
    if (this.labels.length === 0) return;

    const currentLabel = this.labels[this.currentLabelIndex];
    let nextDelay = this.TYPING_SPEED;

    switch (this.animationPhase) {
      case 'typing':
        this.currentCharIndex++;
        this.placeholder.textContent = currentLabel.slice(0, this.currentCharIndex);

        if (this.currentCharIndex >= currentLabel.length) {
          this.animationPhase = 'pause-after-type';
          nextDelay = this.PAUSE_AFTER_TYPE;
        } else {
          nextDelay = this.TYPING_SPEED;
        }
        break;

      case 'pause-after-type':
        this.animationPhase = 'erasing';
        nextDelay = this.ERASING_SPEED;
        break;

      case 'erasing':
        this.currentCharIndex--;
        this.placeholder.textContent = currentLabel.slice(0, this.currentCharIndex);

        if (this.currentCharIndex <= 0) {
          this.placeholder.textContent = '';
          this.currentCharIndex = 0;
          this.currentLabelIndex = (this.currentLabelIndex + 1) % this.labels.length;
          this.animationPhase = 'pause-after-erase';
          nextDelay = this.PAUSE_AFTER_ERASE;
        } else {
          nextDelay = this.ERASING_SPEED;
        }
        break;

      case 'pause-after-erase':
        this.animationPhase = 'typing';
        nextDelay = this.TYPING_SPEED;
        break;
    }

    this.scheduleNext(nextDelay);
  }

  /**
   * Re-collecte les labels et redémarre l'animation.
   * Utilisé par shopify:block:load quand un child block est ajouté/supprimé/modifié.
   * Ne re-bind pas les event listeners (le DOM input n'est pas remplacé).
   */
  reinit() {
    this.pauseAnimation();
    this.placeholder.textContent = '';
    this.currentCharIndex = 0;
    this.currentLabelIndex = 0;
    this.collectLabels();

    if (this.input.value.length === 0) {
      this.form.classList.remove('is-typing');
      this.startAnimation();
    }
  }
}

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-search-bar]').forEach((el) => new SearchBar(el));
});

// ---------------------------------------------------------------------------
// Support éditeur Shopify
// ---------------------------------------------------------------------------
if (window.Shopify?.designMode) {
  // Re-init complète quand la section est rechargée (DOM entier remplacé)
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-search-bar]').forEach((el) => new SearchBar(el));
  });

  // Reinit léger quand un child block change (seul le conteneur content_for re-render)
  document.addEventListener('shopify:block:load', (e) => {
    const searchBar = e.target.querySelector('[data-search-bar]');
    if (searchBar && searchBar.__searchBarInstance) {
      searchBar.__searchBarInstance.reinit();
    }
  });
}
